/**
 * Financial Year and Invoice Formatting Helpers
 * Indian Financial Year: April 1st to March 31st (e.g., April 2026 - March 2027 -> 2026-2027)
 */

/**
 * Computes the Indian Financial Year string (YYYY-YYYY) for a given date.
 * Automatically changes when a new financial year starts (April 1st).
 * @param {string|Date} [dateInput] - Date string or Date object. Defaults to current date.
 * @returns {string} Financial year e.g. "2026-2027"
 */
export const getFinancialYear = (dateInput) => {
  let d;
  if (!dateInput) {
    d = new Date();
  } else if (typeof dateInput === "string") {
    d = new Date(dateInput);
    if (isNaN(d.getTime())) {
      const parts = dateInput.split(/[-/]/);
      if (parts.length === 3) {
        if (parts[0].length === 2 && parts[2].length === 4) {
          d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
      }
    }
  } else {
    d = new Date(dateInput);
  }

  if (!d || isNaN(d.getTime())) {
    d = new Date();
  }

  const year = d.getFullYear();
  const month = d.getMonth() + 1; // 1-indexed (1=Jan, 4=Apr, 12=Dec)

  // If month is April (4) to December (12), FY is year-(year+1)
  // If month is January (1) to March (3), FY is (year-1)-year
  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;

  return `${startYear}-${endYear}`;
};

/**
 * Extracts numeric part from any invoice string (e.g., "INV-1147-8462" -> "8462", "DDT/8363/2026-2027" -> "8363", "8463" -> "8463")
 * @param {string|number} invNo
 * @returns {string}
 */
export const extractInvoiceNumeric = (invNo) => {
  if (!invNo && invNo !== 0) return "";
  const str = String(invNo).trim();
  
  // Format: CODE/NUM/YEAR-YEAR
  const slashMatch = str.match(/^[A-Za-z0-9_-]+\/(\d+)\/\d{4}-\d{4}$/);
  if (slashMatch) return slashMatch[1];

  // Format: INV-1147-8462 or INV-8462 or just 8462
  const dashMatch = str.match(/(?:INV-)?(?:\d+-)?(\d+)$/i);
  if (dashMatch) return dashMatch[1];

  return str.replace(/\D/g, "") || str;
};

/**
 * Formats invoice number to format: [COMPANY_CODE]/[INVOICE_NUM]/[FINANCIAL_YEAR]
 * Example: DDT/8363/2026-2027
 * @param {string|number} invNo - Invoice number or existing display string
 * @param {string|Date} [invDate] - Invoice date to compute financial year
 * @param {string} [companyCode="DDT"] - Company code (default: DDT)
 * @returns {string} Formatted invoice number e.g. "DDT/8363/2026-2027"
 */
export const formatInvoiceNumber = (invNo, invDate, companyCode = "DDT") => {
  if (!invNo && invNo !== 0) return "";
  const str = String(invNo).trim();
  
  const fy = getFinancialYear(invDate);
  const code = (companyCode || "DDT").toString().trim().toUpperCase() || "DDT";

  // If already in CODE/NUM/FY format
  if (/^[A-Za-z0-9_-]+\/\d+\/\d{4}-\d{4}$/.test(str)) {
    return str;
  }

  const num = extractInvoiceNumeric(str);
  if (!num) return str;

  return `${code}/${num}/${fy}`;
};

/**
 * Formats date to DD-MM-YYYY format (e.g. 24-08-2026)
 * @param {string|Date} dateInput
 * @returns {string} Formatted date e.g. "24-08-2026"
 */
export const formatDateToDMY = (dateInput) => {
  if (!dateInput) return "";
  const str = String(dateInput).trim();

  // If already DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;

  let d = new Date(dateInput);
  if (isNaN(d.getTime())) {
    const parts = str.split(/[-/T ]/);
    if (parts.length >= 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        d = new Date(`${parts[0]}-${parts[1]}-${parts[2]}`);
      } else if (parts[2].length === 4) {
        // DD-MM-YYYY
        return `${String(parts[0]).padStart(2, "0")}-${String(parts[1]).padStart(2, "0")}-${parts[2]}`;
      }
    }
  }

  if (isNaN(d.getTime())) return str;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

export default {
  getFinancialYear,
  extractInvoiceNumeric,
  formatInvoiceNumber,
  formatDateToDMY,
};
